import {
  createContext,
  memo,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type {
  Maybe,
  WeddingWebsiteQuery,
  Wws_WeddingWebsiteResult,
} from '../types/graphql';
import { cleanWeddingWebsitePageItemContentData } from '../lib/cleanUserInput';

export interface WWSDataWithResult extends WeddingWebsiteQuery {
  weddingWebsiteResult?: Maybe<Wws_WeddingWebsiteResult>;
}

export interface WWContextValue {
  data: WeddingWebsiteQuery | null;
  setWWData: (wwData: WeddingWebsiteQuery) => void;
  isSinglePageLayout: boolean;
  setSinglePageLayout: (isSinglePageLayout: boolean) => void;
}

interface Props {
  children: ReactNode;
  data: WeddingWebsiteQuery;
}

const initialContext: WWContextValue = {
  data: null,
  isSinglePageLayout: false,
  setSinglePageLayout: (): void => {
    return;
  },
  setWWData: (): void => {
    return;
  },
};

export const WWContext = createContext<WWContextValue>(initialContext);

function WWProviderComponent({ children, data }: Props) {
  const [wwData, setWWData] = useState<WeddingWebsiteQuery>(data);
  const [isSinglePageLayout, setSinglePageLayout] = useState<boolean>(false);

  const cleanedWWData = useMemo<WeddingWebsiteQuery | null>(() => {
    return cleanWeddingWebsitePageItemContentData(wwData);
  }, [wwData]);
  // Picks up data fetched after mount (e.g. the lazy full-data fetch
  // triggered by navigating to a route whose images were stripped from SSR).
  useEffect(() => {
    setWWData(data);
  }, [data]);

  const value = useMemo<WWContextValue>(() => {
    return {
      data: cleanedWWData,
      isSinglePageLayout,
      setSinglePageLayout,
      setWWData,
    };
  }, [cleanedWWData, isSinglePageLayout]);

  return <WWContext.Provider value={value}>{children}</WWContext.Provider>;
}

export const WWProvider = memo(WWProviderComponent);
